import React, { useCallback, useEffect, useRef, useState } from "react";

import { DEFAULT_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH } from "./constants";

import { ResizeHandle } from "./LeftSidebarResizeHandle.styles";

interface LeftSidebarResizeHandleProps {
  isCollapsed: boolean;
  containerRef: React.RefObject<HTMLElement>;
  onResizeEnd: (newWidth: number) => void;
  onCollapse: () => void;
}

/**
 * Resize handle for the left sidebar.
 * Allows users to drag to adjust the sidebar width.
 */
const LeftSidebarResizeHandle: React.FC<LeftSidebarResizeHandleProps> = ({ isCollapsed, containerRef, onResizeEnd, onCollapse }) => {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const hasMovedRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;
      hasMovedRef.current = false; // Reset movement flag

      // Get current width from CSS custom property
      const currentWidth = parseInt(document.documentElement.style.getPropertyValue("--leftSidebarWidth") || String(DEFAULT_SIDEBAR_WIDTH), 10);
      startWidthRef.current = currentWidth;

      // Disable transitions during drag for smooth resizing
      if (containerRef.current) {
        containerRef.current.style.transition = "none";
      }
    },
    [containerRef]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startXRef.current;

      // Track if mouse has moved more than 3px (to distinguish click from drag)
      if (Math.abs(deltaX) > 3) {
        hasMovedRef.current = true;
      }

      // Only enforce max width during drag; clamp at 0 to avoid invalid negative widths
      const newWidth = Math.max(0, Math.min(MAX_SIDEBAR_WIDTH, startWidthRef.current + deltaX));

      // Update CSS variable directly for instant visual feedback (no React re-render)
      document.documentElement.style.setProperty("--leftSidebarWidth", `${newWidth}px`);
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // Re-enable transitions
      if (containerRef.current) {
        containerRef.current.style.transition = "";
      }

      // If mouse didn't move significantly, treat as click to collapse
      if (!hasMovedRef.current) {
        onCollapse();
      } else {
        // Get final width and update React state + localStorage
        const finalWidth = parseInt(document.documentElement.style.getPropertyValue("--leftSidebarWidth") || String(DEFAULT_SIDEBAR_WIDTH), 10);
        onResizeEnd(finalWidth);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, containerRef, onResizeEnd, onCollapse]);

  if (isCollapsed) return null;

  return (
    <ResizeHandle
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      tabIndex={0}
      onMouseDown={handleMouseDown}
      $isDragging={isDragging}
    />
  );
};

export default LeftSidebarResizeHandle;
